/**
 * The public music catalogue and its three views, fetched through the API boundary.
 *
 * WHY THIS IS ASYNC WHEN THE ANSWER IS ALREADY IN MEMORY. lib/discovery could be
 * called straight from the component — it is pure, and it would render in the same
 * tick. But then the seam that a real `GET /discovery/showcase` has to arrive through
 * would not exist, and the day it arrives every call site would need a loading state
 * it had never had. So the request goes through api/discoveryApi like every other read
 * in the app, and this hook owns the effect, because effects belong in the React layer.
 *
 * ONE REQUEST FOR THE WHOLE SCREEN. Featured, Trending and Explore come back together,
 * from one catalogue, in one response — which is what stops the three sections from ever
 * disagreeing about what music exists. The genre filter is part of the same request rather
 * than a second one for Explore alone, for the same reason.
 *
 * THE GENRE SELECTION LIVES HERE, not in the component, because it is a request
 * parameter: it is the one piece of state that changes what comes back. `selectGenre`
 * takes a genre name (or ALL_GENRES) and the effect refetches.
 *
 * LOADED ONCE PER APP SESSION, THEN HELD UNTIL REFRESHED. The catalogue is fetched the first
 * time Home is opened and is NOT refetched when the library changes or when the route is
 * revisited — publishing and ranking changes wait for Refresh, while the per-song Love / play /
 * share counts on the songs already on screen are mirrored live from the store. Leaving Home
 * does not blank it and start over. Only two things request
 * again: `genre`, because the chip is a request parameter and picking one changes what Explore
 * shows, and `attempt`, which `reload()` bumps for the section's Refresh button. Everything
 * else is a snapshot the viewer chooses to refresh, so a ranking cannot reshuffle under them
 * while they are reading it — a track sliding out of Featured the instant it is Loved reads as
 * a glitch, not a feature. The counts themselves can still move in place, which is the difference
 * between "this list re-ranked itself" and "this card's own play total updated."
 *
 * STALE-WHILE-REVALIDATE, deliberately, for the catalogue request that DOES happen. A genre
 * change or a Refresh keeps the previous lists on screen instead of blanking them — without it
 * each one would empty the page for the mock's latency and then repopulate it, which reads as a
 * bug. Only the very first load has nothing to show, and `loading` is true only then.
 *
 * `support` is read synchronously and is not part of the request: what the app can
 * claim about discovery is a fact about this build, not something a response carries.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { discoveryApi, type DiscoverySupport } from '../api/discoveryApi';
import { songsApi } from '../api/songsApi';
import { LYRIXA_DEMO_STATS_ENABLED } from '../config';
import type { Song } from '../types/domain';
import { toApiErrorShape, type ApiErrorShape } from '../api/errors';
import { ALL_GENRES, type Showcase } from '../lib/discovery';
import type { SaveCounts } from '../lib/engagement';
import { rememberSongsForPlayback } from './usePlayer';
import { songSaveCounts, usePlaylistsStore } from '../stores/playlistsStore';
import { useSongsStore } from '../stores/songsStore';

function mirrorLiveSongFields<T extends Song>(song: T, live: Song | undefined): T {
  if (!live) return song;
  if (
    song.favorite === live.favorite &&
    song.playCount === live.playCount &&
    song.shareCount === live.shareCount
  ) {
    return song;
  }
  return {
    ...song,
    favorite: live.favorite,
    playCount: live.playCount,
    shareCount: live.shareCount,
  };
}

function mirrorLiveSongList<T extends Song[]>(songs: T, liveById: ReadonlyMap<string, Song>): T {
  let changed = false;
  const next = songs.map((song) => {
    const mirrored = mirrorLiveSongFields(song, liveById.get(song.id));
    if (mirrored !== song) changed = true;
    return mirrored;
  }) as T;
  return changed ? next : songs;
}

export interface UseShowcase {
  /** Every song eligible for discovery, newest first. The source of truth. */
  catalogue: Showcase['catalogue'];
  /** Genres present in the catalogue, for the filter chips. */
  genres: Showcase['genres'];
  /** The active genre filter. ALL_GENRES when unfiltered. */
  genre: string;
  /** The one hero song, or null for an empty catalogue. */
  featured: Showcase['featured'];
  /** The rest of the featured selection. */
  featuredRail: Showcase['featuredRail'];
  trending: Showcase['trending'];
  /** The catalogue under `genre`, loaded by pages for the Explore rail. */
  explore: Showcase['explore'];
  /** True while another Explore page is loading. */
  exploreLoadingMore: boolean;
  /** True when another Explore page may be fetched. */
  hasMoreExplore: boolean;
  /** True while the section's explicit Refresh request is in flight. */
  refreshing: boolean;
  /**
   * Saves per song id — how many playlists contain each song.
   *
   * RETURNED AS WELL AS SENT, because the cards print it now and not only the Featured
   * ranking consumes it. Computed here either way, so the number on a card and the number
   * behind the ranking are the same count from the same store rather than two readings.
   */
  saves: SaveCounts;
  /** True until the first response arrives. False during a refetch. */
  loading: boolean;
  /**
   * The failure, if the last request failed. Null otherwise.
   *
   * ============================================================================
   * THE STATE THIS HOOK DID NOT HAVE. `getShowcase(...).then(...)` had no rejection path at
   * all, so a failed catalogue read produced an unhandled promise rejection and left
   * `loading` true for the rest of the session — a home page spinning forever, with nothing
   * on screen saying why and no way to try again. And `loading` going false with an empty
   * `showcase` was worse: the Dashboard's "No discoverable music yet" would state, as fact,
   * that nobody has published anything.
   *
   * An `ApiErrorShape` rather than an `Error`, so it is the same normalised shape every
   * store's `LoadState.error` carries and the screen can hand its `message` to the same
   * `LoadError` component the collection screens use. This hook holds `useState` rather than
   * a `LoadState` because the catalogue is not a store — but the states it exposes are the
   * same four, and `error` is the one that was missing.
   * ============================================================================
   */
  error: ApiErrorShape | null;
  /** Re-requests the catalogue, for a retry after a failure. */
  reload: () => void;
  /** Changes the genre filter, which refetches. */
  selectGenre: (genre: string) => void;
  /** The app's one optimistic discovery Love path. */
  toggleLove: (song: Song) => void;
  /** The app's one optimistic discovery share-count path. */
  recordShare: (song: Song) => void;
  /** Loads the next Explore page, if any. */
  loadMoreExplore: () => void;
  /** What the sections may and may not claim. Never changes at runtime. */
  support: DiscoverySupport;
}

/** Empty until the first response, so the shape is stable from the first render. */
const NOTHING_YET: Showcase = {
  catalogue: [],
  genres: [],
  genre: ALL_GENRES,
  featured: null,
  featuredRail: [],
  trending: [],
  explore: [],
};

type ExplorePage = Awaited<ReturnType<typeof discoveryApi.getExploreSongs>>;

function firstExplorePage(showcase: Showcase, limit: number = 8): ExplorePage {
  const items = showcase.explore.slice(0, limit);
  return {
    items,
    nextCursor: items.length === limit && showcase.explore[limit] ? String(items[items.length - 1]?.seq ?? '') : null,
  };
}

interface ShowcaseSessionSnapshot {
  genre: string;
  showcase: Showcase | null;
  error: ApiErrorShape | null;
  explore: Showcase['explore'];
  hasMoreExplore: boolean;
  exploreCursor: string | null;
}

function emptySessionSnapshot(): ShowcaseSessionSnapshot {
  return {
    genre: ALL_GENRES,
    showcase: null,
    error: null,
    explore: [],
    hasMoreExplore: false,
    exploreCursor: null,
  };
}

let sessionSnapshot = emptySessionSnapshot();

function rememberSessionSnapshot(next: Partial<ShowcaseSessionSnapshot>): void {
  sessionSnapshot = { ...sessionSnapshot, ...next };
}

function rememberSuccessfulRead(genre: string, showcase: Showcase, page: ExplorePage): void {
  sessionSnapshot = {
    genre,
    showcase,
    error: null,
    explore: page.items,
    hasMoreExplore: page.nextCursor !== null,
    exploreCursor: page.nextCursor,
  };
}

export function resetShowcaseSession(): void {
  sessionSnapshot = emptySessionSnapshot();
}

export function useShowcase(): UseShowcase {
  const [, setSimulationTick] = useState(0);
  useEffect(() => {
    if (!LYRIXA_DEMO_STATS_ENABLED) return;
    const timer = window.setInterval(() => setSimulationTick((tick) => tick + 1), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  /*
    THE SAVE SIGNAL FOR THE FEATURED RANKING, and the reason this hook subscribes to the
    playlists store. A save in this app is a playlist membership, so how often a song has been
    saved is a fact the playlists state holds and nothing else does — see
    stores/playlistsStore.songSaveCounts and lib/engagement.SaveCounts. One field per selector
    call, per Zustand v5's Object.is rule. The subscription is what keeps the `saves` count on
    the cards live even though the effect no longer refetches on it — see the effect and the
    `saves` memo below.
  */
  const playlists = usePlaylistsStore((s) => s.playlists);
  const songs = useSongsStore((s) => s.songs);
  const [genre, setGenre] = useState(() => sessionSnapshot.genre);
  const [showcase, setShowcase] = useState<Showcase | null>(() => sessionSnapshot.showcase);
  const [error, setError] = useState<ApiErrorShape | null>(() => sessionSnapshot.error);
  const [explore, setExplore] = useState<Showcase['explore']>(() => sessionSnapshot.explore);
  const [hasMoreExplore, setHasMoreExplore] = useState(() => sessionSnapshot.hasMoreExplore);
  const [exploreLoadingMore, setExploreLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  /*
    A counter, not a boolean: `reload()` has to refetch even when nothing else has changed,
    and incrementing a dependency is how an effect is asked to run again. Nothing renders it.
  */
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => {
    setRefreshing(true);
    setAttempt((n) => n + 1);
  }, []);
  const exploreCursorRef = useRef<string | null>(sessionSnapshot.exploreCursor);
  const setSongReaction = useCallback((songId: string, patch: Partial<Showcase['catalogue'][number]>) => {
    setShowcase((current) => {
      if (!current) return current;
      const apply = <T extends Song[]>(songs: T): T =>
        songs.map((song) => (song.id === songId ? { ...song, ...patch } : song)) as T;
      const nextFeatured = current.featured?.id === songId ? { ...current.featured, ...patch } : current.featured;
      const nextShowcase = {
        ...current,
        catalogue: apply(current.catalogue),
        featured: nextFeatured,
        featuredRail: apply(current.featuredRail),
        trending: apply(current.trending),
        explore: apply(current.explore),
      };
      setExplore((items) => {
        const nextExplore = apply(items);
        rememberSessionSnapshot({ showcase: nextShowcase, explore: nextExplore });
        return nextExplore;
      });
      return nextShowcase;
    });
  }, []);
  const toggleLove = useCallback(
    (song: Song) => {
      const loved = !song.favorite;
      setSongReaction(song.id, { favorite: loved });
      useSongsStore.setState((current) => ({
        songs: current.songs.map((candidate) =>
          candidate.id === song.id ? { ...candidate, favorite: loved } : candidate
        ),
      }));
      void songsApi.setDiscoveryLove(song.id, loved);
    },
    [setSongReaction]
  );
  const recordShare = useCallback(
    (song: Song) => {
      setSongReaction(song.id, { shareCount: (song.shareCount ?? 0) + 1 });
    },
    [setSongReaction]
  );
  const loadMoreExplore = useCallback(() => {
    if (exploreLoadingMore || !hasMoreExplore) return;
    setExploreLoadingMore(true);
    void discoveryApi
      .getExploreSongs({ genre, limit: 8, cursor: exploreCursorRef.current ?? undefined })
      .then((page) => {
        setExplore((current) => {
          const nextExplore = [...current, ...page.items];
          rememberSessionSnapshot({
            explore: nextExplore,
            hasMoreExplore: page.nextCursor !== null,
            exploreCursor: page.nextCursor,
          });
          return nextExplore;
        });
        exploreCursorRef.current = page.nextCursor;
        setHasMoreExplore(page.nextCursor !== null);
      })
      .finally(() => setExploreLoadingMore(false));
  }, [exploreLoadingMore, hasMoreExplore, genre]);

  /*
    Memoised because it is now RETURNED as well as sent: a fresh object on every render would
    be a new prop identity for every card. The effect below still counts inside itself rather
    than depending on this, so the request cannot be retriggered by an identity change.
  */
  const saves = useMemo(() => songSaveCounts(playlists), [playlists]);

  useEffect(() => {
    const liveById = new Map(songs.map((song) => [song.id, song] as const));
    let nextShowcase: Showcase | null = null;

    setShowcase((current) => {
      if (!current) return current;

      const nextCatalogue = mirrorLiveSongList(current.catalogue, liveById);
      const nextFeatured = current.featured
        ? mirrorLiveSongFields(current.featured, liveById.get(current.featured.id))
        : null;
      const nextFeaturedRail = mirrorLiveSongList(current.featuredRail, liveById);
      const nextTrending = mirrorLiveSongList(current.trending, liveById);
      const nextExplore = mirrorLiveSongList(current.explore, liveById);

      if (
        nextCatalogue === current.catalogue &&
        nextFeatured === current.featured &&
        nextFeaturedRail === current.featuredRail &&
        nextTrending === current.trending &&
        nextExplore === current.explore
      ) {
        nextShowcase = current;
        return current;
      }

      nextShowcase = {
        ...current,
        catalogue: nextCatalogue,
        featured: nextFeatured,
        featuredRail: nextFeaturedRail,
        trending: nextTrending,
        explore: nextExplore,
      };
      return nextShowcase;
    });

    setExplore((current) => {
      const nextExplore = mirrorLiveSongList(current, liveById);
      if (nextShowcase && (nextShowcase !== showcase || nextExplore !== current)) {
        rememberSessionSnapshot({ showcase: nextShowcase, explore: nextExplore });
      }
      return nextExplore;
    });
  }, [songs, showcase]);

  useEffect(() => {
    /*
      REUSE THE HELD SNAPSHOT when Home is revisited unchanged. The first open for a genre owns
      the request; coming back to the route should show that same snapshot immediately, and only
      the explicit Refresh button should ask again. A changed genre or a bumped `attempt` still
      performs the read below, because those are the two actions that mean "show me a different
      catalogue now".
    */
    if (attempt === 0 && sessionSnapshot.showcase !== null && sessionSnapshot.genre === genre) {
      setShowcase(sessionSnapshot.showcase);
      setExplore(sessionSnapshot.explore);
      setHasMoreExplore(sessionSnapshot.hasMoreExplore);
      exploreCursorRef.current = sessionSnapshot.exploreCursor;
      setError(sessionSnapshot.error);
      setRefreshing(false);
      rememberSongsForPlayback(sessionSnapshot.showcase.catalogue);
      return;
    }

    // Guarded so a response for a previous request cannot land after a newer one has been
    // made — two chip presses in quick succession would otherwise race, and the slower
    // reply would win, leaving the grid showing a genre other than the selected chip.
    let cancelled = false;
    /*
      NO LIBRARY IS SENT (Batch 7 §1.6). This used to pass `songs`, which made the browser the
      authoritative source of public visibility — whatever this hook handed over was the public
      catalogue. The read now queries the catalogue itself (api/mockCatalogue today, a real
      endpoint later) and applies the eligibility rule at the boundary.

      THE LIBRARY IS NO LONGER A DEPENDENCY EITHER, which is the load-once rule: `songs` and
      `playlists` are not in the dep list, so a publish, a Love, a play or a save does not
      refetch. What mount loaded stays until `genre` changes or the Refresh button bumps
      `attempt`. Saves are still SENT — read fresh from the store on the line below rather than
      from a captured value — so a Refresh ranks on the save counts as they are when it is pressed.
    */
    discoveryApi.getShowcase({ genre, saves: songSaveCounts(usePlaylistsStore.getState().playlists) }).then(
      (next) => {
        if (cancelled) return;
        const page = firstExplorePage(next);
        rememberSongsForPlayback(next.catalogue);
        setShowcase(next);
        setExplore(page.items);
        exploreCursorRef.current = page.nextCursor;
        setHasMoreExplore(page.nextCursor !== null);
        // Cleared on success, so a recovered read does not keep showing the old failure.
        setError(null);
        setRefreshing(false);
        rememberSuccessfulRead(genre, next, page);
      },
      (err: unknown) => {
        /*
          THE REJECTION PATH, which did not exist. Without it a failed read was an unhandled
          rejection and `showcase` stayed null forever — `loading` never went false, so the
          home page's catalogue spun for the rest of the session.

          `showcase` is deliberately left alone: stale-while-revalidate applies to a failed
          refetch too, so a catalogue already on screen stays there and the screen decides
          whether it has anything to show beside the notice.
        */
        if (!cancelled) {
          const nextError = toApiErrorShape(err);
          setError(nextError);
          setRefreshing(false);
          rememberSessionSnapshot({ error: nextError });
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [genre, attempt]);

  const current = showcase ?? NOTHING_YET;
  return {
    catalogue: current.catalogue,
    genres: current.genres,
    /*
      The SELECTED genre, not the one the last response was built with. The chips have to
      light up the moment they are pressed rather than after the mock's latency — a filter
      that takes a third of a second to acknowledge a press reads as a dropped click. What
      the response carries is which genre the *grid* is currently showing, and
      stale-while-revalidate means that is briefly the previous one by design.
    */
    genre,
    featured: current.featured,
    featuredRail: current.featuredRail,
    trending: current.trending,
    explore,
    exploreLoadingMore,
    hasMoreExplore,
    refreshing,
    saves,
    /*
      `&& error === null` is the load-bearing half. A first request that FAILED leaves
      `showcase` null, so "null means still loading" would report a permanent loading state
      for a request that has already finished — the spinner-forever bug in a different place.
      Loading means no answer yet; a failure is an answer.
    */
    loading: showcase === null && error === null,
    error,
    reload,
    selectGenre: setGenre,
    toggleLove,
    recordShare,
    loadMoreExplore,
    support: discoveryApi.discoverySupport(),
  };
}
